const CONSENT_VERSION = '2026-07-privacy-v1';
const CONSENT_STORAGE_KEY = 'nucleop:privacy-consent';
const CONSENT_EVENT = 'nucleop:privacy-consent-change';

const OPTIONAL_CATEGORIES = Object.freeze(['analytics', 'marketing']);
const ESSENTIAL_CONSENT = Object.freeze({ essential: true, analytics: false, marketing: false });
const CATEGORY_LABELS = Object.freeze({
  analytics: ['analyticsTitle', 'analyticsDescription'],
  marketing: ['marketingTitle', 'marketingDescription'],
});
const REQUIRED_BASE_LABELS = Object.freeze([
  'title',
  'summary',
  'acceptAll',
  'rejectAll',
  'customize',
  'save',
  'essentialTitle',
  'essentialDescription',
]);

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' ? value : null;
  } catch (_) {
    return null;
  }
};

const getStorage = () => {
  try {
    return window.localStorage || null;
  } catch (_) {
    return null;
  }
};

const normalizeConsent = (value = {}) => ({
  version: String(value.version || CONSENT_VERSION),
  decidedAt: value.decidedAt || null,
  categories: {
    ...ESSENTIAL_CONSENT,
    ...Object.fromEntries(OPTIONAL_CATEGORIES.map((key) => [key, value.categories?.[key] === true])),
  },
});

export const getConsent = () => {
  const raw = getStorage()?.getItem(CONSENT_STORAGE_KEY);
  const parsed = normalizeConsent(safeParse(raw) || {});
  return parsed.version === CONSENT_VERSION && parsed.decidedAt ? parsed : null;
};

export const hasConsent = (category) => {
  if (category === 'essential') return true;
  return getConsent()?.categories?.[category] === true;
};

const removeCookie = (name) => {
  const hostname = String(window.location?.hostname || '').trim();
  const domainParts = hostname.split('.').filter(Boolean);
  const domains = new Set(['']);

  for (let index = 0; index < domainParts.length - 1; index += 1) {
    domains.add(`.${domainParts.slice(index).join('.')}`);
  }

  ['/', window.location?.pathname || '/'].forEach((path) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};${domain ? ` domain=${domain};` : ''} SameSite=Lax`;
    });
  });
};

export const clearOptionalTrackingStorage = () => {
  [
    '_ga',
    '_gid',
    '_gat',
    '_gcl_au',
    '_clck',
    '_clsk',
    'CLID',
    'MUID',
    'ANONCHK',
    'SM',
  ].forEach(removeCookie);
};

export const saveConsent = (categories = {}) => {
  const consent = normalizeConsent({
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    categories,
  });
  getStorage()?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));

  if (!consent.categories.analytics && !consent.categories.marketing) {
    clearOptionalTrackingStorage();
  }

  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
  return consent;
};

const el = (tag, attrs = {}, children = []) => {
  const node = document.createElement(tag);
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === undefined || value === null || value === false) return;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key.startsWith('on') && typeof value === 'function') node.addEventListener(key.slice(2), value);
    else node.setAttribute(key, String(value));
  });
  children.filter(Boolean).forEach((child) => node.appendChild(child));
  return node;
};

const normalizeOptionalCategories = (categories) => {
  if (!Array.isArray(categories)) return OPTIONAL_CATEGORIES;
  const unique = categories.filter((key, index) => (
    OPTIONAL_CATEGORIES.includes(key) && categories.indexOf(key) === index
  ));
  return unique.length > 0 ? unique : OPTIONAL_CATEGORIES;
};

const getRequiredLabels = (optionalCategories) => [
  ...REQUIRED_BASE_LABELS,
  ...optionalCategories.flatMap((category) => CATEGORY_LABELS[category] || []),
];

const categoryRow = ({ key, title, description, checked = false, disabled = false }) => {
  const input = el('input', {
    class: 'privacy-consent-toggle-input',
    type: 'checkbox',
    name: key,
    checked: checked ? 'checked' : null,
    disabled: disabled ? 'disabled' : null,
  });

  return el('label', {
    class: `privacy-consent-category${disabled ? ' disabled' : ''}`,
  }, [
    el('span', { class: 'privacy-consent-category-copy' }, [
      el('strong', { text: title }),
      el('span', { text: description }),
    ]),
    input,
    el('span', { class: 'privacy-consent-toggle', 'aria-hidden': 'true' }),
  ]);
};

const buildDialog = ({ labels, optionalCategories, privacyUrl, onSave }) => {
  const current = getConsent();
  const optionalRows = optionalCategories.map((category) => categoryRow({
    key: category,
    title: labels[`${category}Title`],
    description: labels[`${category}Description`],
    checked: current?.categories?.[category] === true,
  }));
  const panel = el('div', { class: 'privacy-consent-options', hidden: 'hidden' }, [
    categoryRow({
      key: 'essential',
      title: labels.essentialTitle,
      description: labels.essentialDescription,
      checked: true,
      disabled: true,
    }),
    ...optionalRows,
  ]);

  const saveFromPanel = () => {
    const categories = Object.fromEntries(OPTIONAL_CATEGORIES.map((category) => [category, false]));
    optionalRows.forEach((row, index) => {
      categories[optionalCategories[index]] = row.querySelector('input')?.checked === true;
    });
    onSave(categories);
  };

  const selectAllOptional = (checked) => Object.fromEntries(
    OPTIONAL_CATEGORIES.map((category) => [category, checked && optionalCategories.includes(category)]),
  );

  const dialog = el('section', {
    class: 'privacy-consent',
    role: 'dialog',
    'aria-live': 'polite',
    'aria-label': labels.title,
  }, [
    el('div', { class: 'privacy-consent-copy' }, [
      el('h2', { text: labels.title }),
      el('p', { text: labels.summary }),
      privacyUrl ? el('a', { href: privacyUrl, target: '_blank', rel: 'noopener noreferrer', text: labels.privacy }) : null,
    ]),
    panel,
    el('div', { class: 'privacy-consent-actions' }, [
      el('button', { type: 'button', class: 'ep-button privacy-consent-button', text: labels.rejectAll, onclick: () => onSave(selectAllOptional(false)) }),
      el('button', {
        type: 'button',
        class: 'ep-button privacy-consent-button',
        text: labels.customize,
        onclick: () => {
          panel.removeAttribute('hidden');
        },
      }),
      el('button', { type: 'button', class: 'ep-button privacy-consent-button primary', text: labels.acceptAll, onclick: () => onSave(selectAllOptional(true)) }),
      el('button', { type: 'button', class: 'ep-button privacy-consent-button save', text: labels.save, onclick: saveFromPanel }),
    ]),
  ]);

  return dialog;
};

export const showConsentBanner = (options = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return getConsent();
  if (getConsent() && options.force !== true) return getConsent();

  document.querySelector('.privacy-consent')?.remove();
  const labels = options.labels && typeof options.labels === 'object' ? options.labels : null;
  const optionalCategories = normalizeOptionalCategories(options.optionalCategories);
  const missingLabels = getRequiredLabels(optionalCategories).filter((key) => !String(labels?.[key] || '').trim());
  if (missingLabels.length > 0) {
    console.warn('[privacy] Dicionário de privacidade incompleto.', missingLabels);
    return getConsent();
  }

  const dialog = buildDialog({
    labels,
    optionalCategories,
    privacyUrl: options.privacyUrl || '',
    onSave: (categories) => {
      const consent = saveConsent(categories);
      dialog.remove();
      if (typeof options.onChange === 'function') options.onChange(consent);
    },
  });

  document.body.appendChild(dialog);
  return getConsent();
};

export const onConsentChange = (handler) => {
  if (typeof handler !== 'function') return () => {};
  const listener = (event) => handler(event.detail);
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
};

export const openConsentPreferences = (options = {}) => showConsentBanner({ ...options, force: true });

if (typeof window !== 'undefined') {
  window.NucleopPrivacy = {
    getConsent,
    hasConsent,
    showPreferences: openConsentPreferences,
    clearOptionalTrackingStorage,
  };
}
